import React from 'react';
import { Box, Typography, Avatar, Badge } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { getFileCategory, formatTime } from './chatUtils';
import OnlineDot from './OnlineDot';

const UserListItem = ({
  user,
  isSelected,
  onClick,
  lastMessage,
  userDetails,
  isOnline,
  unreadCount = 0,
}) => {
  const theme = useTheme();
  const fullName =
    `${user?.fname || ''} ${user?.lname || ''}`.trim() ||
    user?.email?.split('@')[0] ||
    'User';
  const isDark = theme.palette.mode === 'dark';
  const hasUnread = unreadCount > 0;

  const previewText = () => {
    if (!lastMessage) return 'Start a conversation...';
    const isSelf = lastMessage.senderId?._id === userDetails?.id;
    const prefix = isSelf ? 'You: ' : '';
    if (lastMessage.fileAttachment) {
      const cat = getFileCategory(lastMessage.fileAttachment.fileType);
      const labels = {
        image: '🖼 Photo',
        video: '🎥 Video',
        pdf: '📄 PDF',
        excel: '📊 Spreadsheet',
        word: '📝 Document',
        other: '📎 File',
      };
      return `${prefix}${labels[cat] || '📎 File'}`;
    }
    return `${prefix}${lastMessage.message}`;
  };

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.5,
        cursor: 'pointer',
        borderRadius: 2,
        mx: 1,
        mb: 0.5,
        transition: 'all 0.2s ease',
        bgcolor: isSelected
          ? isDark
            ? alpha(theme.palette.primary.main, 0.2)
            : alpha(theme.palette.primary.main, 0.08)
          : 'transparent',
        border: '1px solid',
        borderColor: isSelected ? 'primary.main' : 'transparent',
        '&:hover': {
          bgcolor: isSelected
            ? isDark
              ? alpha(theme.palette.primary.main, 0.22)
              : alpha(theme.palette.primary.main, 0.1)
            : isDark
            ? alpha(theme.palette.common.white, 0.05)
            : alpha(theme.palette.common.black, 0.04),
        },
      }}
    >
      <Box sx={{ position: 'relative', flexShrink: 0 }}>
        <Badge
          badgeContent={hasUnread ? (unreadCount > 99 ? '99+' : unreadCount) : 0}
          color="error"
          invisible={!hasUnread}
          sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', minWidth: 18, height: 18 } }}
        >
          <Avatar
            src={user?.image || undefined}
            alt={fullName}
            sx={{
              width: 46,
              height: 46,
              fontSize: '1.1rem',
              fontWeight: 700,
              bgcolor: isSelected
                ? 'primary.main'
                : isDark
                ? alpha(theme.palette.primary.main, 0.4)
                : 'primary.lighter',
              color: isSelected ? 'white' : 'primary.dark',
            }}
          >
            {!user?.image && fullName.charAt(0).toUpperCase()}
          </Avatar>
        </Badge>
        <OnlineDot isOnline={isOnline} paperBg={theme.palette.background.paper} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.25 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: hasUnread || isSelected ? 700 : 600,
              color: isSelected ? 'primary.main' : 'text.primary',
              fontSize: '0.875rem',
              textTransform: 'capitalize',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '130px',
            }}
          >
            {fullName}
          </Typography>
          {lastMessage?.timestamp && (
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', flexShrink: 0 }}>
              {formatTime(lastMessage.timestamp)}
            </Typography>
          )}
        </Box>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontSize: '0.75rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'block',
            maxWidth: '170px',
            fontStyle: lastMessage ? 'normal' : 'italic',
            fontWeight: hasUnread ? 600 : 400,
          }}
        >
          {previewText()}
        </Typography>
      </Box>
    </Box>
  );
};

export default UserListItem;
